// Markdown → React through @cortex/markdown: the same reading the PDF export uses, rendered as
// elements (never HTML strings), so nothing a guide says can become markup or script.
import { assetName, parseMarkdown, safeHref, type Block, type Inline } from '@cortex/markdown';
import * as React from 'react';

export type AssetRenderer = (name: string, alt: string, inline: boolean) => React.ReactNode;
/**
 * Renders a link the page handles itself (an in-page reference such as a vault wikilink); null = the
 * default: a safe external link in a new tab.
 */
export type LinkRenderer = (href: string, children: React.ReactNode) => React.ReactNode | null;

interface Renderers {
  asset: AssetRenderer;
  link?: LinkRenderer | undefined;
}

function inlines(nodes: readonly Inline[], r: Renderers): React.ReactNode[] {
  const { asset } = r;
  return nodes.map((n, i) => {
    switch (n.type) {
      case 'text':
        return <React.Fragment key={i}>{n.text}</React.Fragment>;
      case 'strong':
        return <strong key={i}>{inlines(n.children, r)}</strong>;
      case 'emph':
        return <em key={i}>{inlines(n.children, r)}</em>;
      case 'strike':
        return <s key={i}>{inlines(n.children, r)}</s>;
      case 'code':
        return (
          <code key={i} className="px-1 py-0.5 rounded bg-card-border/40 text-[0.9em] font-mono">
            {n.text}
          </code>
        );
      case 'break':
        return <br key={i} />;
      case 'link': {
        const href = safeHref(n.href);
        const body = inlines(n.children, r);
        const own = href && r.link ? r.link(href, body) : null;
        if (own) return <React.Fragment key={i}>{own}</React.Fragment>;
        return href ? (
          <a
            key={i}
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            className="text-nav-active underline"
          >
            {body}
          </a>
        ) : (
          <React.Fragment key={i}>{body}</React.Fragment>
        );
      }
      case 'image': {
        const name = assetName(n.src);
        return (
          <React.Fragment key={i}>
            {name ? asset(name, n.alt, true) : <em className="text-page-muted">[image: {n.alt || n.src}]</em>}
          </React.Fragment>
        );
      }
    }
  });
}

const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' } as const;

function blocks(bs: readonly Block[], r: Renderers, headingOffset: number): React.ReactNode[] {
  const { asset } = r;
  return bs.map((b, i) => {
    switch (b.type) {
      case 'heading': {
        const level = Math.min(6, b.level + headingOffset);
        const cls =
          level <= 2
            ? 'text-lg font-semibold mt-4 mb-2'
            : level === 3
              ? 'text-base font-semibold mt-3 mb-1.5'
              : 'text-sm font-semibold mt-3 mb-1';
        return React.createElement(
          `h${level}`,
          { key: i, className: `${cls} text-page-text` },
          inlines(b.children, r),
        );
      }
      case 'paragraph': {
        const only = b.children.length === 1 ? b.children[0] : undefined;
        if (only?.type === 'image') {
          const name = assetName(only.src);
          if (name)
            return (
              <figure key={i} className="my-3">
                {asset(name, only.alt, false)}
                {only.alt ? (
                  <figcaption className="text-xs text-page-muted mt-1 text-center">{only.alt}</figcaption>
                ) : null}
              </figure>
            );
        }
        return (
          <p key={i} className="my-2 leading-relaxed">
            {inlines(b.children, r)}
          </p>
        );
      }
      case 'code':
        return (
          <pre
            key={i}
            className="my-2 rounded-md px-3 py-2 text-xs font-mono overflow-x-auto bg-card-border/30"
          >
            <code>{b.text}</code>
          </pre>
        );
      case 'quote':
        return (
          <blockquote key={i} className="my-2 border-l-2 border-divider pl-3 text-page-muted">
            {blocks(b.children, r, headingOffset)}
          </blockquote>
        );
      case 'list': {
        const items = b.items.map((item, j) => (
          <li key={j} className="my-0.5 [&>p]:my-0">
            {blocks(item, r, headingOffset)}
          </li>
        ));
        return b.ordered ? (
          <ol key={i} start={b.start} className="my-2 pl-6 list-decimal">
            {items}
          </ol>
        ) : (
          <ul key={i} className="my-2 pl-6 list-disc">
            {items}
          </ul>
        );
      }
      case 'table':
        return (
          <div key={i} className="my-2 overflow-x-auto">
            <table className="text-sm border-collapse">
              <thead>
                <tr>
                  {b.header.map((h, j) => (
                    <th
                      key={j}
                      className={`border border-divider px-2 py-1 font-semibold ${ALIGN[b.align[j] ?? 'left'] ?? ''}`}
                    >
                      {inlines(h, r)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, j) => (
                  <tr key={j}>
                    {row.map((c, k) => (
                      <td
                        key={k}
                        className={`border border-divider px-2 py-1 ${ALIGN[b.align[k] ?? 'left'] ?? ''}`}
                      >
                        {inlines(c, r)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'rule':
        return <hr key={i} className="my-3 border-divider" />;
    }
  });
}

/** Renders a Markdown section body; headings sit one level below the section's own heading. */
export function Markdown({
  source,
  asset,
  headingOffset = 1,
  link,
}: {
  source: string;
  asset: AssetRenderer;
  headingOffset?: number;
  /** Optional: renders the links the page handles itself (an in-page reference). */
  link?: LinkRenderer;
}) {
  const tree = React.useMemo(() => parseMarkdown(source), [source]);
  return <div className="text-sm text-page-text">{blocks(tree, { asset, link }, headingOffset)}</div>;
}
